import { useTranslation } from '@spcc/core'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from '@spcc/uikit'
import type { SalesOrder, SalesOrdersSortBy, SalesOrdersSortOrder } from '../../../../domain/types'
import { SalesOrderRow } from './sales-order-row'

interface SalesOrdersTableProps {
  salesOrders: SalesOrder[]
  selectedRowId: string | null
  showTenantColumn?: boolean
  sortField?: SalesOrdersSortBy
  sortOrder?: SalesOrdersSortOrder
  onSort?: (field: SalesOrdersSortBy) => void
  onRowClick: (salesOrder: SalesOrder) => void
  onRowDoubleClick: (salesOrder: SalesOrder) => void
  onView: (salesOrder: SalesOrder) => void
  onEdit: (salesOrder: SalesOrder) => void
  onReorder: (salesOrder: SalesOrder) => void
  onCancel: (salesOrder: SalesOrder) => void
}

interface ColumnConfig {
  key: string
  labelKey: string
  sortKey?: SalesOrdersSortBy
  align?: 'start' | 'center' | 'end'
  width?: string
}

function SortableHeader({
  label,
  sortKey,
  currentSortField,
  currentSortOrder,
  onSort,
  align = 'start',
}: {
  label: string
  sortKey?: SalesOrdersSortBy
  currentSortField?: SalesOrdersSortBy
  currentSortOrder?: SalesOrdersSortOrder
  onSort?: (field: SalesOrdersSortBy) => void
  align?: 'start' | 'center' | 'end'
}) {
  const isSortable = !!sortKey && !!onSort
  const isActive = sortKey === currentSortField

  const handleClick = () => {
    if (isSortable && sortKey) {
      onSort(sortKey)
    }
  }

  const SortIcon = isActive
    ? currentSortOrder === 'asc'
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown

  return (
    <TableHead
      className={cn(
        'text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider',
        isSortable && 'cursor-pointer hover:text-foreground hover:bg-muted/30 transition-colors select-none',
        isActive && 'text-foreground',
        align === 'end' && 'text-end'
      )}
      onClick={isSortable ? handleClick : undefined}
    >
      <div className={cn('flex items-center gap-1.5', align === 'end' && 'justify-end')}>
        <span>{label}</span>
        {isSortable && (
          <SortIcon
            className={cn(
              'w-3.5 h-3.5 shrink-0 transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground/50'
            )}
          />
        )}
      </div>
    </TableHead>
  )
}

export function SalesOrdersTable({
  salesOrders,
  selectedRowId,
  showTenantColumn,
  sortField,
  sortOrder,
  onSort,
  onRowClick,
  onRowDoubleClick,
  onView,
  onEdit,
  onReorder,
  onCancel,
}: SalesOrdersTableProps) {
  const { t } = useTranslation()

  const columns: ColumnConfig[] = [
    { key: 'salesOrder', labelKey: 'salesOrders.salesOrder', sortKey: 'salesOrderNumber' },
    ...(showTenantColumn ? [{ key: 'tenant', labelKey: 'common.tenant' }] : []),
    { key: 'factory', labelKey: 'salesOrders.factory' },
    { key: 'products', labelKey: 'salesOrders.products' },
    { key: 'consumption', labelKey: 'salesOrders.consumption' },
    { key: 'orders', labelKey: 'salesOrders.orders' },
    { key: 'status', labelKey: 'salesOrders.statusLabel', sortKey: 'status' },
    { key: 'actions', labelKey: 'salesOrders.actions', align: 'end' as const, width: '180px' },
  ]

  return (
    <div className="glass-card rounded-2xl overflow-hidden border border-border/50" data-testid="sales-orders-table">
      <Table>
        <TableHeader>
          <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
            {columns.map((col) => (
              <SortableHeader
                key={col.key}
                label={t(col.labelKey)}
                sortKey={col.sortKey}
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSort={onSort}
                align={col.align}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {salesOrders.map((salesOrder) => (
            <SalesOrderRow
              key={salesOrder.id}
              salesOrder={salesOrder}
              isSelected={selectedRowId === salesOrder.id}
              showTenantColumn={showTenantColumn}
              onRowClick={onRowClick}
              onRowDoubleClick={onRowDoubleClick}
              onView={onView}
              onEdit={onEdit}
              onReorder={onReorder}
              onCancel={onCancel}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
